import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import CatalogDialogHeader from 'modules/CatalogManagement/components/CatalogDialogHeader';
import NdcAutocomplete from 'modules/StockManagement/components/NdcAutocomplete';

import { REQUIRED_TEXT_FIELDS, normalizeNdc } from '../utils/productPricingHelpers';

const EMPTY_SELECTION = { ndc: '', isSelected: false, option: null };
const EMPTY_FIELDS = { name: '', strength: '', dgDrugName: '', code: '', coldShipped: '' };

// Adds one product to the grid. The NDC has to be picked from the full inventory master (stocked
// or not, expired or not, deactivated or not), and one already in the grid can't be picked again.
// Every required column is asked for here, so a new row never lands in the grid with blanks; the
// optional fees are filled in on the grid afterwards.
// allowedNdcs, when given, limits the pick to those NDCs: an invoice group can only override a
// product that's already in the default price list.
const AddProductPricingDialog = ({ open, existingNdcs, allowedNdcs, onClose, onAdd }) => {
  const [selection, setSelection] = useState(EMPTY_SELECTION);
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [unitPrice, setUnitPrice] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;

    setSelection(EMPTY_SELECTION);
    setFields(EMPTY_FIELDS);
    setUnitPrice('');
    setSubmitted(false);
  }, [open]);

  const ndc = normalizeNdc(selection.ndc);
  const isDuplicate = selection.isSelected && existingNdcs.has(ndc);
  const isNotAllowed = selection.isSelected && !!allowedNdcs && !allowedNdcs.has(ndc);
  const unitPriceNumber = Number(unitPrice);
  const isUnitPriceValid =
    unitPrice !== '' && Number.isFinite(unitPriceNumber) && unitPriceNumber >= 0;
  const missingFields = REQUIRED_TEXT_FIELDS.filter(({ field }) => !fields[field].trim());

  let ndcError = '';
  if (isNotAllowed) {
    ndcError = `NDC ${ndc} isn't in the default price list`;
  } else if (isDuplicate) {
    ndcError = `NDC ${ndc} is already in the price list`;
  } else if (submitted && !selection.isSelected) {
    ndcError = 'Pick an NDC from inventory';
  }

  const handleNdcChange = ({ ndc: nextNdc, isSelected, option }) => {
    setSelection({ ndc: nextNdc, isSelected, option });

    // Prefill Name and Strength from the picked inventory item, in the price sheet's upper case
    // (the inventory API title-cases drug fields). Both stay editable.
    if (isSelected && option) {
      setFields((current) => ({
        ...current,
        name: option.drugName?.toUpperCase() ?? '',
        strength: option.strength?.toUpperCase() ?? '',
      }));
    }
  };

  const setField = (field) => (event) =>
    setFields((current) => ({ ...current, [field]: event.target.value }));

  const handleAdd = () => {
    setSubmitted(true);

    if (
      !selection.isSelected ||
      isNotAllowed ||
      isDuplicate ||
      !isUnitPriceValid ||
      missingFields.length
    ) {
      return;
    }

    onAdd({
      ndc,
      name: fields.name.trim(),
      strength: fields.strength.trim(),
      dgDrugName: fields.dgDrugName.trim(),
      code: fields.code.trim(),
      coldShipped: fields.coldShipped,
      legendFee: null,
      multiVialFee: null,
      batchFee: null,
      suppliesFee: null,
      unitPrice: unitPriceNumber,
    });
  };

  const requiredError = (field) => submitted && !fields[field].trim();

  const renderTextField = (field, label) => (
    <TextField
      size='small'
      label={`${label} *`}
      value={fields[field]}
      onChange={setField(field)}
      error={requiredError(field)}
      helperText={requiredError(field) ? 'Required' : ' '}
      sx={{ flex: 1 }}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <CatalogDialogHeader title='Add Product' onClose={onClose} />

      <DialogContent>
        <Stack spacing={1} sx={{ mt: 2 }}>
          <NdcAutocomplete
            label='NDC / Drug Name *'
            value={selection.ndc}
            onChange={handleNdcChange}
            error={!!ndcError}
            helperText={
              ndcError ||
              (allowedNdcs
                ? 'Only products in the default price list can be overridden'
                : 'Search inventory by NDC or drug name (3+ characters)')
            }
            includeInactive
            getOptionDisabledReason={(option) => {
              const optionNdc = normalizeNdc(option.ndc);
              if (allowedNdcs && !allowedNdcs.has(optionNdc))
                return 'Not in the default price list';
              return existingNdcs.has(optionNdc) ? 'Already in the price list' : null;
            }}
          />
          <Stack direction='row' spacing={2}>
            {renderTextField('name', 'Name')}
            {renderTextField('strength', 'Strength')}
          </Stack>
          <Stack direction='row'>{renderTextField('dgDrugName', 'DG_Drug_Name')}</Stack>
          <Stack direction='row' spacing={2}>
            {renderTextField('code', 'Code')}
            <TextField
              select
              size='small'
              label='Cold_Shipped *'
              value={fields.coldShipped}
              onChange={setField('coldShipped')}
              error={requiredError('coldShipped')}
              helperText={requiredError('coldShipped') ? 'Required' : ' '}
              sx={{ flex: 1 }}
            >
              <MenuItem value='Y'>Y</MenuItem>
              <MenuItem value='N'>N</MenuItem>
            </TextField>
          </Stack>
          <TextField
            size='small'
            label='Unit_Price *'
            type='number'
            value={unitPrice}
            onChange={(event) => setUnitPrice(event.target.value)}
            error={submitted && !isUnitPriceValid}
            helperText={submitted && !isUnitPriceValid ? 'Enter a unit price of 0 or more' : ' '}
            InputProps={{
              startAdornment: <InputAdornment position='start'>$</InputAdornment>,
              inputProps: { min: 0, step: 'any' },
            }}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={handleAdd}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddProductPricingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  existingNdcs: PropTypes.instanceOf(Set).isRequired,
  allowedNdcs: PropTypes.instanceOf(Set),
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

AddProductPricingDialog.defaultProps = {
  allowedNdcs: null,
};

export default AddProductPricingDialog;
